/**
 * City-level insights fetching for the local homepage — pure data fetch, no
 * React state. There is no neighbourhood-level stats endpoint, so this is
 * always called with the city a search resolves to, never the exact search.
 */

import { getCityStats, getTopCities } from 'data/insights/fetcher';
import { fetchWithRetry } from 'utils/fetchWithRetry';
import { LocalFocusGenerationObservabilityService } from 'observability/services/local-focus-generation-observability';
import type { ProvinceOrStateCode } from 'utils/province_or_state';
import type { CountryCode } from 'types/countries';
import type { CityStats } from 'data/insights/types';
import type { AreaCardData } from '../../local-focus/area-cards';
import { cityInsightsDataSchema } from '../../local-focus/city-insights';
import type { CityInsightsData } from '../../local-focus/city-insights';
import { topCitiesToAreaCards } from './derivation';
import {
  selectLocalizedCityInsights,
  isLocaleTranslationMissing,
  normalizeLocaleToInsightsLang,
  type StoredCityInsights,
} from 'utils/city-insights/localize';

export interface CityLevelInsightsData {
  stats: CityStats | null;
  popularAreaCards: AreaCardData[];
  /**
   * City insights already resolved to the requested `locale`. When the locale's
   * translation is missing, the AI prose (`overview`/`aiOverview`) is stripped
   * so only the Overview card is hidden — the numeric cards still render.
   */
  cityInsights: CityInsightsData | null;
  /**
   * True when nothing is stored for the slug yet, or a row exists but lacks the
   * requested locale's translation. The caller uses this to trigger on-demand
   * generation for client-resolved locations (which the server-side
   * `insightsGenerateLocale` does not cover).
   */
  insightsTranslationMissing: boolean;
}

/**
 * GET the persisted (raw) city-insights row for a slug. Returns the validated
 * stored payload, or null when absent/unreachable/invalid. The raw payload is
 * kept (not the render schema) so any per-locale `translations` survive for
 * locale selection by callers.
 */
async function fetchStoredCityInsights(slug: string): Promise<StoredCityInsights | null> {
  try {
    const res = await fetchWithRetry(`/api/city-insights/${slug}`);
    if (!res.ok) return null;
    const json = await res.json();
    const parsed = cityInsightsDataSchema.safeParse(json);
    return parsed.success ? (json as StoredCityInsights) : null;
  } catch {
    return null;
  }
}

export async function fetchCityLevelInsightsData(
  country: CountryCode,
  province: string,
  city: string,
  slug: string,
  locale?: string
): Promise<CityLevelInsightsData> {
  const provinceCode = province as ProvinceOrStateCode;

  const [stats, topCities, storedCityInsights] = await Promise.all([
    (async () => {
      let s = city ? await getCityStats(country, provinceCode, city) : undefined;
      if (!s) s = await getCityStats(country, provinceCode, undefined);
      return s ?? null;
    })(),
    (city
      ? getTopCities(country, provinceCode, undefined, 5, false, undefined, undefined, city)
      : getTopCities(country, provinceCode, undefined, 5)
    ).catch(() => undefined),
    slug ? fetchStoredCityInsights(slug) : Promise.resolve(null),
  ]);

  return {
    stats,
    popularAreaCards: topCities?.length ? topCitiesToAreaCards(topCities, city || province.toUpperCase()) : [],
    cityInsights: storedCityInsights ? selectLocalizedCityInsights(storedCityInsights, locale) : null,
    insightsTranslationMissing: !storedCityInsights || isLocaleTranslationMissing(storedCityInsights, locale),
  };
}

/**
 * Fire-and-forget: asks the server to generate/translate a persisted
 * city-insights row for the requested locale, so it's ready on the next visit.
 * `needsGeneration` is true when nothing is stored yet, or a row exists but is
 * missing the requested locale's translation. De-duplicated per `${slug}:${lang}`
 * via the caller-owned `triggeredKeys` set.
 */
export function triggerInsightGenerationIfNeeded(
  slug: string,
  city: string,
  province: string,
  country: CountryCode,
  needsGeneration: boolean,
  locale: string | undefined,
  triggeredKeys: Set<string>
): void {
  if (!slug || !needsGeneration) return;
  const lang = normalizeLocaleToInsightsLang(locale);
  const dedupeKey = `${slug}:${lang}`;
  if (triggeredKeys.has(dedupeKey)) return;
  triggeredKeys.add(dedupeKey);
  LocalFocusGenerationObservabilityService.trackCityInsightsGenerationTriggered({ slug, city, province, country });
  fetch(`/api/city-insights/${encodeURIComponent(slug)}/generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ city, province, country, locale: lang }),
    keepalive: true,
  }).catch(() => {/* swallow — server is the source of truth */});
}

/** Default cadence and ceiling for city-insights generation polling (~30s). */
const INSIGHTS_POLL_INTERVAL_MS = 2000;
const INSIGHTS_POLL_MAX_ATTEMPTS = 15;

export interface PollForCityInsightsOptions {
  slug: string;
  locale?: string;
  intervalMs?: number;
  maxAttempts?: number;
  signal?: AbortSignal;
}

/**
 * Resolve after `ms`, or immediately if `signal` aborts first.
 * Returns true when it was aborted, false when the delay elapsed normally.
 */
function delay(ms: number, signal?: AbortSignal): Promise<boolean> {
  return new Promise(resolve => {
    if (signal?.aborted) return resolve(true);
    const onAbort = () => {
      clearTimeout(timer);
      resolve(true);
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve(false);
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Poll the persisted city-insights row until the requested locale's content is
 * ready (background generation finished) or the attempt budget is exhausted.
 * Resolves with the localized insights when ready, otherwise null. Never throws.
 *
 * Waits `intervalMs` before the first check to give generation time to run, and
 * stops early when `signal` aborts.
 */
export async function pollForCityInsights({
  slug,
  locale,
  intervalMs = INSIGHTS_POLL_INTERVAL_MS,
  maxAttempts = INSIGHTS_POLL_MAX_ATTEMPTS,
  signal,
}: PollForCityInsightsOptions): Promise<CityInsightsData | null> {
  if (!slug) return null;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const aborted = await delay(intervalMs, signal);
    if (aborted) return null;

    const stored = await fetchStoredCityInsights(slug);
    if (stored && !isLocaleTranslationMissing(stored, locale)) {
      return selectLocalizedCityInsights(stored, locale);
    }
  }

  return null;
}
